/**
 * The net basis — what does NOT reach the account.
 *
 * Five deductions, each typed absence (`null`) until the reader states it, so a
 * scenario with none of them evaluates to exactly the gross figures the page
 * printed before this file existed (UE-AC-055). Per CHARGE, never a flat
 * percentage: the fixed part of a provider fee is why a $4.99 weekly charge
 * loses more of itself than a $119.99 annual one (UE-AC-056).
 *
 * Provider fee SCHEME (2026-09-22 plan, D2/D3): a card processor and an app
 * store do not compute their cut the same way. A processor's percentage lands
 * on the amount actually charged and keeps its cut through a refund; a store's
 * percentage lands on the price net of tax, gives its cut back on a refund,
 * treats a dispute exactly like a refund (no per-event fee), and — for the
 * standard 30 % tier — drops to 15 % once a subscriber has paid for more than
 * a year. `FeeScheme` is that shape; `PROCESSOR_SCHEME`/`STORE_SCHEME` are the
 * two book defaults a preset starts from (Task 1 stamps each preset with one).
 *
 * Imports only `./product` on purpose — `model.ts` imports this file, and a
 * cycle between the two would leave one of them reading uninitialised bindings
 * under jest's CommonJS transform. The trial prior is therefore an ARGUMENT.
 */
import { defaultChecks, maxChargesPerYear, trialOf, type UnitProduct } from './product';

/**
 * How a provider's fee behaves — the switches a preset carries and a stated
 * fee KEEPS through edits (unlike `preset`, which drops the moment a number is
 * typed). Absent in a document or a link = `PROCESSOR_SCHEME`, which is the
 * arithmetic this file had before the scheme existed (I2).
 */
export interface FeeScheme {
  /** The amount the percentage applies to: the price net of tax, or the amount actually charged. */
  base: 'charged' | 'exTax';
  /** What happens to the provider's own cut when the charge it was taken from is refunded. */
  onRefund: 'retained' | 'returned';
  /** How a dispute is treated: a flat per-event fee on top of the loss, or exactly like a refund. */
  onDispute: 'chargeback' | 'refund';
  /** The percentage past `maxChargesPerYear` charges of paid service (Apple's post-year-one 15 %); `null` = the year-one rate never changes. */
  afterYearPct: number | null;
}

/** A card processor / merchant of record: on the charged amount, keeps its cut through a refund, a dispute costs a flat fee. */
export const PROCESSOR_SCHEME: FeeScheme = { base: 'charged', onRefund: 'retained', onDispute: 'chargeback', afterYearPct: null };
/** An app store: on the price net of tax, gives its cut back on a refund, a dispute is a refund with no fee of its own. */
export const STORE_SCHEME: FeeScheme = { base: 'exTax', onRefund: 'returned', onDispute: 'refund', afterYearPct: null };

/**
 * Field-by-field equality — two schemes are the same only while every one of
 * these agrees. `afterYearPct` compares within `1e-9` (D2): the link grammar
 * writes it as a rounded percent and reads it back divided by 100, and a
 * scheme parsed off a link must still equal the preset's own literal for the
 * preset's NAME to survive the round trip (`presetIdFor`).
 */
export const sameScheme = (a: FeeScheme, b: FeeScheme): boolean =>
  a.base === b.base &&
  a.onRefund === b.onRefund &&
  a.onDispute === b.onDispute &&
  (a.afterYearPct === null && b.afterYearPct === null
    ? true
    : a.afterYearPct !== null && b.afterYearPct !== null && Math.abs(a.afterYearPct - b.afterYearPct) < 1e-9);

export interface FeeDeduction {
  /** Fraction of the scheme's base amount (the charged amount under a processor scheme, ex-tax under a store one). */
  pct: number;
  /** Money per charge, in the scenario's currency. */
  fixed: number;
  /** The benchmark-book preset this was filled from, or `null` once a field was edited. */
  preset: string | null;
  /** How this provider computes its cut (D2) — copied from the preset that filled `pct`/`fixed`, kept through an edit. */
  scheme: FeeScheme;
}

export interface TaxDeduction {
  /** `true` = the sticker price already contains the tax; `false` = it is added on top. */
  included: boolean;
  rate: number;
}

export interface RefundsDeduction {
  /** Share of PAYERS whose first charge is refunded. */
  share: number;
}

export interface DisputesDeduction {
  /** Share of PAYERS who dispute; each dispute loses the ex-tax first charge and `fee`. */
  share: number;
  fee: number;
}

/**
 * Activation — «opened the app after paying» (Task 14, 2026-09-21). A share of
 * paying customers opens the app; the rest — non-openers — renew less, so they
 * pay fewer of the charges a product's own cadence would otherwise count.
 */
export interface ActivationDeduction {
  /** Share of paying customers who open the app after paying. */
  share: number;
  /** Charges the REST — non-openers — pay before they stop renewing. */
  nonOpenerChecks: number;
}

/**
 * A user-named custom fee (controller ruling R-1, 2026-09-21). This file
 * carries the type and the arithmetic (`feeOnCharge` stacks every custom fee
 * on the charged amount beside the provider's); the widget state, the `fees=`
 * link grammar and both hosts' UI for them shipped in Task 8b.
 */
export interface CustomFee {
  id: string;
  name: string;
  pct: number;
  fixed: number;
}

export const MAX_CUSTOM_FEES = 8;
export const CUSTOM_FEE_NAME_MAX = 40;

export interface Deductions {
  fee: FeeDeduction | null;
  tax: TaxDeduction | null;
  refunds: RefundsDeduction | null;
  disputes: DisputesDeduction | null;
  /** «Opened the app after paying» (Task 14) — non-openers pay fewer charges. */
  activation: ActivationDeduction | null;
  /** User-named custom fees (R-1); `[]` = none. Always present — not a `DeductionKind`. */
  custom: CustomFee[];
}

/** The five STATED-or-absent deductions. `custom` is a list, not a kind — it is never absent, only empty. */
export type DeductionKind = 'fee' | 'tax' | 'refunds' | 'disputes' | 'activation';
export const DEDUCTION_KINDS: readonly DeductionKind[] = ['fee', 'tax', 'refunds', 'disputes', 'activation'];

/**
 * A `Deductions`-shaped fee whose `scheme` may be ABSENT.
 *
 * Every live `FeeDeduction` (a widget's `state.deductions`, an `evaluate()`
 * input) requires `scheme` (D2), and since Task 4 of the 2026-09-22
 * provider-scheme plan the scenario document does too — `scenario.schema.json`
 * declares it and `serializeScenario` writes it always. `parseScenario`
 * resolves a stated fee's scheme itself before returning (the wire's own
 * `fee.scheme`, else the named preset's, else `PROCESSOR_SCHEME` — D-8), so a
 * `ScenarioDocument` it hands back never needs this type; `ScenarioDocument.
 * deductions` is a plain `Deductions`.
 *
 * This type stays for the one remaining door a scheme can still be missing
 * through: a `ScenarioDocument`-shaped object built by hand or by a served
 * seam OUTSIDE `parseScenario` (an admin seam, a fixture). `clampDeductions`
 * (`unit-economics-widget/widgetState.ts`) is what turns one of those into a
 * full `Deductions`, filling the same D-8 default.
 */
export type DocumentFeeDeduction = Omit<FeeDeduction, 'scheme'> & { scheme?: FeeScheme };

/** A `Deductions` that may still be missing `fee.scheme` — see `DocumentFeeDeduction`. */
export type DocumentDeductions = Omit<Deductions, 'fee'> & { fee: DocumentFeeDeduction | null };

/** What one evaluation took off the gross, per pressed user. */
export interface DeductedTotals {
  fee: number;
  tax: number;
  refunds: number;
  disputes: number;
  /** Value lost to non-openers' fewer charges (Task 14) — on the basis in force. */
  activation: number;
}

export const emptyDeductions = (): Deductions => ({
  fee: null,
  tax: null,
  refunds: null,
  disputes: null,
  activation: null,
  custom: [],
});

/** Accepts a `DocumentDeductions` too (a `scheme`-optional fee changes nothing here — only presence/absence is read). */
export const hasDeductions = (deductions: Deductions | DocumentDeductions | null | undefined): boolean =>
  deductions !== null &&
  deductions !== undefined &&
  (DEDUCTION_KINDS.some(kind => deductions[kind] !== null) || deductions.custom.length > 0);

/** What the customer's card is charged for a sticker price. */
export const chargedAmount = (price: number, tax: TaxDeduction | null): number =>
  tax !== null && !tax.included ? price * (1 + tax.rate) : price;

/** The revenue inside a sticker price once the tax is out of it. */
export const exTaxAmount = (price: number, tax: TaxDeduction | null): number =>
  tax !== null && tax.included ? price / (1 + tax.rate) : price;

/** The amount a fee's percentage applies to, under its own scheme — ex-tax for a store, charged for a processor. */
export const feeBaseAmount = (price: number, tax: TaxDeduction | null, scheme: FeeScheme): number =>
  scheme.base === 'exTax' ? exTaxAmount(price, tax) : chargedAmount(price, tax);

/** The provider's own cut of ONE charge at the year-one rate. */
export const providerFeeOnCharge = (price: number, deductions: Deductions): number => {
  const fee = deductions.fee;
  if (fee === null) return 0;
  return fee.pct * feeBaseAmount(price, deductions.tax, fee.scheme) + fee.fixed;
};

/**
 * The provider's cut of `count` EXPECTED charges of one product. `firstYear` is
 * the charges the cadence fits inside twelve months (`maxChargesPerYear`); the
 * expected charges past it are at `afterYearPct` when the scheme names one.
 *
 * On the 12-month basis `count ≤ firstYear` holds for every cadence EXCEPT a
 * plan billed exactly once a year (§ D9, 2026-09-22 plan): its first renewal
 * is due on day 365, so a reader may count it (`maxChargesCounted` lets the
 * knob reach `2`) while `firstYear` — `maxChargesPerYear`, unchanged — stays
 * `1`. There the second term is the renewal's OWN charge, priced at
 * `afterYearPct` (UE-AC-117: an annual plan under the App Store preset at
 * `count` 1.6 pays `0.30·B·1 + 0.15·B·0.6`). For every other cadence this
 * still degrades to `count × providerFeeOnCharge` (I5).
 */
export const providerFeeOnCharges = (price: number, count: number, deductions: Deductions, firstYear: number): number => {
  const fee = deductions.fee;
  if (fee === null) return 0;
  const base = feeBaseAmount(price, deductions.tax, fee.scheme);
  const later = fee.scheme.afterYearPct === null ? 0 : Math.max(0, count - firstYear);
  const early = count - later;
  return fee.pct * base * early + (fee.scheme.afterYearPct ?? 0) * base * later + fee.fixed * count;
};

/** Every user-named custom fee (R-1) on ONE charge — always on the charged amount, always retained. */
export const customFeesOnCharge = (price: number, deductions: Deductions): number => {
  const charged = chargedAmount(price, deductions.tax);
  return deductions.custom.reduce((sum, fee) => sum + (fee.pct * charged + fee.fixed), 0);
};

/** The providers' cut of ONE charge — the provider's own fee (`providerFeeOnCharge`) plus every user-named custom fee (R-1); they stack, in any order. */
export const feeOnCharge = (price: number, deductions: Deductions): number =>
  providerFeeOnCharge(price, deductions) + customFeesOnCharge(price, deductions);

/** What one charge of a sticker price leaves after tax and every provider fee. */
export const netCharge = (price: number, deductions: Deductions): number =>
  exTaxAmount(price, deductions.tax) - feeOnCharge(price, deductions);

/**
 * What a refund gives back on ONE charge, under the provider's own scheme
 * (D3, 2026-09-22 plan): the ex-tax amount, minus the provider's own cut of
 * it IF the scheme returns that cut on a refund (`onRefund: 'returned'`) —
 * kept otherwise (`'retained'`, `PROCESSOR_SCHEME` — today's N0 arithmetic,
 * unchanged). A custom fee is never returned, under any scheme (I6). No fee
 * stated → the whole ex-tax amount, as before this plan.
 */
export const refundLossOnCharge = (price: number, deductions: Deductions): number => {
  const returned = deductions.fee?.scheme.onRefund === 'returned';
  return exTaxAmount(price, deductions.tax) - (returned ? providerFeeOnCharge(price, deductions) : 0);
};

/**
 * What a dispute costs on ONE charge, under the provider's own scheme (D3):
 * exactly a refund loss when the scheme treats a dispute AS a refund
 * (`onDispute: 'refund'` — a store; no fee of its own), else the ex-tax
 * amount plus the flat `disputes.fee` on top (`'chargeback'` —
 * `PROCESSOR_SCHEME`, today's N0 arithmetic, unchanged).
 */
export const disputeLossOnCharge = (price: number, deductions: Deductions): number =>
  deductions.fee?.scheme.onDispute === 'refund'
    ? refundLossOnCharge(price, deductions)
    : exTaxAmount(price, deductions.tax) + (deductions.disputes?.fee ?? 0);

/**
 * What `count` expected charges of a sticker price leave after tax and every
 * fee, on the year-one/after-year split `providerFeeOnCharges` applies to the
 * provider's own cut — custom fees stay flat per charge under every scheme.
 * With the processor scheme (`afterYearPct: null`) this is exactly
 * `count × netCharge` (I2), because `providerFeeOnCharges` then degrades to
 * `count × providerFeeOnCharge`.
 */
export const netCharges = (price: number, count: number, deductions: Deductions, firstYear: number): number =>
  exTaxAmount(price, deductions.tax) * count -
  providerFeeOnCharges(price, count, deductions, firstYear) -
  customFeesOnCharge(price, deductions) * count;

/** The first amount a payer is charged — the intro price on a paid trial, the price otherwise. */
export const firstChargeOf = (product: UnitProduct): number => {
  const trial = trialOf(product);
  return trial.kind === 'paid' ? trial.price ?? 0 : product.price;
};

const recurringOf = (product: UnitProduct, basis: 'first' | 'ltv12', checks: Record<string, number>): number => {
  const productChecks = checks[product.id] ?? defaultChecks(product);
  return basis === 'ltv12' && product.type === 'subscription' ? productChecks : 1;
};

/** A reading of `count` expected charges of a price; `firstYear` = charges the cadence fits in twelve months. */
export type ChargeReader = (price: number, count: number, firstYear: number) => number;

/**
 * The value of one accepted user, with a caller-chosen reading of EACH GROUP of
 * charges (the intro tap, then the recurring count). `(p, n) => p * n` is the
 * gross value (what `productValue` prints); `(p, n, y) => netCharges(p, n, d,
 * y)` is the net one; `(_, n) => n` counts the charges. Same arithmetic in the
 * same order for every reading, so gross and net can never disagree about the
 * shape of a product. `firstYear` (`maxChargesPerYear`) travels through so a
 * reader that needs the year-one/after-year split (`providerFeeOnCharges`)
 * never has to recompute it.
 *
 * `checksOverride` (Task 14, 2026-09-21) caps the recurring count at the given
 * number of charges — activation's own non-opener reading (R-18): a non-opener
 * pays `min(nonOpenerChecks, n)` charges instead of the product's usual `n`.
 * `undefined` leaves the normal count untouched. The intro charge of a paid
 * trial is unaffected either way — it is paid once, at the tap, by everyone.
 */
export function productValueWith(
  product: UnitProduct,
  basis: 'first' | 'ltv12',
  checks: Record<string, number>,
  trialConv: Record<string, number>,
  trialConvPrior: number,
  read: ChargeReader,
  checksOverride?: number,
): number {
  const recurring0 = recurringOf(product, basis, checks);
  const recurring = checksOverride === undefined ? recurring0 : Math.min(recurring0, checksOverride);
  const firstYear = maxChargesPerYear(product);
  const trial = trialOf(product);
  if (trial.kind === 'none') return read(product.price, recurring, firstYear);
  const conv = trialConv[product.id] ?? trialConvPrior;
  if (trial.kind === 'free') return conv * read(product.price, recurring, firstYear);
  return read(trial.price ?? 0, 1, firstYear) + conv * read(product.price, recurring, firstYear);
}

/**
 * The net value of one accepted user, blended over activation when it is
 * stated (Task 14): `share × net(n) + (1 − share) × net(min(nonOpenerChecks,
 * n))` — openers get the product's usual charge count, non-openers stop
 * renewing sooner. `read` is `netCharges`, which already degrades to the
 * gross price when no fee/tax is stated, so the blend is correct on whichever
 * basis is in force without any special-casing here.
 */
export const productNetValue = (
  product: UnitProduct,
  basis: 'first' | 'ltv12',
  checks: Record<string, number>,
  trialConv: Record<string, number>,
  trialConvPrior: number,
  deductions: Deductions,
): number => {
  const read: ChargeReader = (p, n, y) => netCharges(p, n, deductions, y);
  const activation = deductions.activation;
  if (activation === null) return productValueWith(product, basis, checks, trialConv, trialConvPrior, read);
  const opened = productValueWith(product, basis, checks, trialConv, trialConvPrior, read);
  const stayed = productValueWith(
    product,
    basis,
    checks,
    trialConv,
    trialConvPrior,
    read,
    activation.nonOpenerChecks,
  );
  return activation.share * opened + (1 - activation.share) * stayed;
};

/** Charges per accepted user: the intro (paid trial) plus the converters' regular charges. */
export const productCharges = (
  product: UnitProduct,
  basis: 'first' | 'ltv12',
  checks: Record<string, number>,
  trialConv: Record<string, number>,
  trialConvPrior: number,
): number => productValueWith(product, basis, checks, trialConv, trialConvPrior, (_, n) => n);

/**
 * The figure every KPI, verdict and break-even reads: net once anything is stated —
 * a deduction in the fold OR a product's own refund rate (P-11) — gross otherwise.
 * `evaluate` decides `netBasis`, because only it sees both.
 */
export const basisValue = (evaluation: { value: number; valueNet: number; netBasis: boolean }): number =>
  evaluation.netBasis ? evaluation.valueNet : evaluation.value;
